import { type ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { GoogleLogo } from '@/modules/auth/components/GoogleLogo';
import { LinkedInLogo } from '@/modules/auth/components/LinkedInLogo';
import {
  businessEmailRequiredMessage,
  trialOfferFailureMessage,
  trialOfferParamKey,
} from '@/modules/auth/constants';
import type { OAuthFailureKind } from '@/modules/auth/oauthCallbackFailure';
import type { AuthIntent } from '@/modules/auth/types/externalLogin';
import { AuthLayout } from '@/modules/layouts/components/authLayout/AuthLayout';
import { DocumentMetadata } from '@/modules/layouts/components/DocumentMetadata';
import { SlackLogo } from '@/modules/signup-flow/components/slackIntegration/SlackLogo';
import { Button } from '@/modules/ui/components/button/Button';
import {
  ProviderMark,
  type ProviderMarkStatus,
} from '@/pages/login/providerMark/ProviderMark';

export type OAuthCallbackProvider = 'Google' | 'Slack' | 'LinkedIn';

const logos: Record<OAuthCallbackProvider, ReactNode> = {
  Google: <GoogleLogo className="size-7" />,
  Slack: <SlackLogo className="size-7" />,
  LinkedIn: <LinkedInLogo className="size-7 text-[#0A66C2]" />,
};

/** How long a sign-in may take before the screen offers a way out. */
const SLOW_AFTER_MS = 15_000;
/** Most sign-ins finish faster than this, so their card never flashes on screen. */
const REVEAL_AFTER_MS = 200;

type Phase = 'pending' | 'slow' | OAuthFailureKind;

interface PhaseCopy {
  heading: string;
  subheading: string;
  /** `alert` for errors, `status` for neutral news; screen readers announce both. */
  announce?: 'alert' | 'status';
  mark: ProviderMarkStatus;
  /** Label of the button that restarts the sign-in; no button when absent. */
  retryLabel?: string;
}

const getCopy = (provider: OAuthCallbackProvider, phase: Phase): PhaseCopy => {
  const tryAgain = `Try again with ${provider}`;

  switch (phase) {
    case 'pending':
      return {
        heading: 'Signing you in',
        subheading: `Finishing your ${provider} sign-in…`,
        mark: 'pending',
      };
    case 'slow':
      return {
        heading: 'Still signing you in',
        subheading:
          'This is taking longer than usual. Try again, or go back and use another sign-in method.',
        announce: 'status',
        mark: 'pending',
        retryLabel: tryAgain,
      };
    case 'cancelled':
      return {
        heading: 'Sign-in cancelled',
        subheading: `You left ${provider} before finishing. Try again, or use another sign-in method.`,
        announce: 'status',
        mark: 'idle',
        retryLabel: `Continue with ${provider}`,
      };
    case 'businessEmail':
      return {
        heading: 'Use your work email',
        subheading: businessEmailRequiredMessage,
        announce: 'alert',
        mark: 'failed',
        retryLabel:
          provider === 'Google' ? 'Choose another Google account' : tryAgain,
      };
    case 'trialOffer':
      return {
        heading: 'Offer unavailable',
        subheading: trialOfferFailureMessage,
        announce: 'alert',
        mark: 'failed',
      };
    case 'expired':
      return {
        heading: `${provider} sign-in expired`,
        subheading:
          'This sign-in timed out or was opened in another browser. Start it again.',
        announce: 'alert',
        mark: 'failed',
        retryLabel: tryAgain,
      };
    case 'failed':
      return {
        heading: `${provider} sign-in failed`,
        subheading: `Something went wrong finishing your ${provider} sign-in. Try again, or use another sign-in method.`,
        announce: 'alert',
        mark: 'failed',
        retryLabel: tryAgain,
      };
  }
};

const withOffer = (path: string, trialOfferCode?: string) =>
  trialOfferCode
    ? `${path}?${new URLSearchParams({ [trialOfferParamKey]: trialOfferCode })}`
    : path;

interface OAuthCallbackStatusProps {
  provider: OAuthCallbackProvider;
  /** Why the sign-in didn't work; absent while it's still being exchanged. */
  failure?: OAuthFailureKind;
  /** Which page started the flow, for the way back; unknown when the stored state is gone. */
  intent?: AuthIntent;
  /** The sign-up link's offer code, kept on the way back unless it was refused. */
  trialOfferCode?: string;
  /** Restarts the sign-in at the provider. Absent when the stored request is gone. */
  onRetry?: () => void | Promise<void>;
  /** Overrides for stories. */
  slowAfterMs?: number;
  revealAfterMs?: number;
}

/**
 * What an OAuth callback (`/auth/callback/google`, `/slack`, `/linkedin`)
 * shows while it signs the user in, when that takes too long, and why it
 * didn't work. Every state renders the same layout, so moving between them
 * changes the copy and the mark's badge rather than swapping the page.
 */
export const OAuthCallbackStatus = ({
  provider,
  failure,
  intent,
  trialOfferCode: linkOfferCode,
  onRetry,
  slowAfterMs = SLOW_AFTER_MS,
  revealAfterMs = REVEAL_AFTER_MS,
}: OAuthCallbackStatusProps) => {
  const [isSlow, setIsSlow] = useState(false);
  const [isRevealed, setIsRevealed] = useState(revealAfterMs <= 0);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    if (failure) return;
    const reveal = setTimeout(() => setIsRevealed(true), revealAfterMs);
    const slow = setTimeout(() => setIsSlow(true), slowAfterMs);
    return () => {
      clearTimeout(reveal);
      clearTimeout(slow);
    };
  }, [failure, revealAfterMs, slowAfterMs]);

  // Back from the provider through the browser's back-forward cache (iOS
  // Safari) restores this page as it was left, retry still spinning.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setIsRetrying(false);
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  const phase: Phase = failure ?? (isSlow ? 'slow' : 'pending');
  const copy = getCopy(provider, phase);

  if (!failure && !isRevealed) {
    return <div className="min-h-svh bg-[#fafafa]" />;
  }

  // A refused offer would fail again, so the way back drops it.
  const trialOfferCode = phase === 'trialOffer' ? undefined : linkOfferCode;
  const back =
    intent === 'signUp'
      ? { label: 'Back to sign up', to: withOffer('/sign-up', trialOfferCode) }
      : { label: 'Back to sign in', to: '/login' };
  const switchLink = !failure
    ? undefined
    : intent === 'signUp'
      ? { prompt: 'Already have an account?', action: 'Log in', to: '/login' }
      : {
          prompt: 'New to Finlay?',
          action: 'Create an account',
          to: withOffer('/sign-up', trialOfferCode),
        };

  const canRetry = Boolean(copy.retryLabel && onRetry);
  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await onRetry?.();
    } catch {
      setIsRetrying(false);
    }
  };

  const showActions = phase !== 'pending';

  return (
    <div className="transition-opacity duration-150 ease-out starting:opacity-0">
      <DocumentMetadata title={copy.heading} />
      <AuthLayout
        mark={
          <ProviderMark
            provider={provider}
            logo={logos[provider]}
            status={copy.mark}
          />
        }
        heading={copy.heading}
        subheading={
          <span key={phase} role={copy.announce}>
            {copy.subheading}
          </span>
        }
        switchLink={switchLink}
      >
        {showActions && (
          <div
            key={phase}
            className="space-y-2 transition-[opacity,translate] duration-150 ease-(--ease-out-strong) starting:translate-y-1 starting:opacity-0 motion-reduce:starting:translate-y-0"
          >
            {canRetry && (
              <Button
                variant="primary"
                loading={isRetrying}
                onClick={handleRetry}
                className="h-9 w-full rounded-md active:scale-[0.98]"
              >
                {copy.retryLabel}
              </Button>
            )}
            {phase === 'trialOffer' && (
              <Button
                variant="primary"
                className="h-9 w-full rounded-md active:scale-[0.98]"
                asChild
              >
                <Link to="/sign-up">Sign up without the offer</Link>
              </Button>
            )}
            {phase !== 'trialOffer' && (
              <Button
                variant={canRetry ? 'ghost' : 'primary'}
                className="h-9 w-full rounded-md active:scale-[0.98]"
                asChild
              >
                <Link to={back.to}>{back.label}</Link>
              </Button>
            )}
          </div>
        )}
      </AuthLayout>
    </div>
  );
};
